import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
  Message,
} from '@arco-design/web-react'
import { IconPlus } from '@arco-design/web-react/icon'
import { updateUserAdminApi, type AdminUser } from '@/api/user'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import { getFormValidateErrorMessage } from '@/utils/helpers'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import {
  displayAmountToQuota,
  getCurrencyConfig,
  quotaToDisplayAmount,
  renderQuota,
} from '@/utils/quota'
import { modalWidth } from '@/utils/responsive'

interface Props {
  visible: boolean
  user: AdminUser | null
  groupOptions?: string[]
  onCancel: () => void
  onSuccess?: () => void
}

export default function EditUserModal({
  visible,
  user,
  groupOptions = [],
  onCancel,
  onSuccess,
}: Props) {
  const { isMobile } = useBreakpoint()
  const [form] = Form.useForm()
  const [addQuotaOpen, setAddQuotaOpen] = useState(false)
  const [addQuotaDelta, setAddQuotaDelta] = useState<number | undefined>()
  const [addAmountDelta, setAddAmountDelta] = useState<number | undefined>()

  const mergedGroupOptions = useMemo(() => {
    const groups = new Set(groupOptions)
    if (user?.group) groups.add(user.group)
    groups.add('default')
    return Array.from(groups)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
  }, [groupOptions, user?.group])

  const currencyConfig = getCurrencyConfig()
  const showAmountInput = currencyConfig.type !== 'TOKENS'

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        id: user.id,
        username: user.username,
        displayName: user.displayName,
        email: user.email,
        group: user.group,
        quota: user.quota,
        role: user.role,
        remark: user.remark || '',
        password: '',
      })
    }
  }, [visible, user, form])

  const { runSecure, verifyVisible, verifyScope, verifyContext, handleSuccess, handleCancel } =
    useSecureVerification()

  const submit = async () => {
    try {
      const values = await form.validate()
      const payload: Record<string, unknown> = { ...values }
      if (!payload.password) delete payload.password
      // 改密码 / 改权限矩阵时后端要求 admin.user.update 的二次验证凭据
      await runSecure(
        (proof) => updateUserAdminApi(payload as Partial<AdminUser> & { id: number }, proof),
        {
          scope: 'admin.user.update',
          context: { user_id: payload.id as number },
        }
      )
      Message.success('已保存')
      onSuccess?.()
      onCancel()
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const applyAddQuota = () => {
    const current = Number(form.getFieldValue('quota') || 0)
    const delta = Number(addQuotaDelta || 0)
    if (!Number.isFinite(delta) || delta === 0) {
      Message.warning('请输入有效的额度增量')
      return
    }
    form.setFieldValue('quota', current + delta)
    setAddQuotaOpen(false)
    setAddQuotaDelta(undefined)
    setAddAmountDelta(undefined)
  }

  const currentQuota = Number(form.getFieldValue('quota') || user?.quota || 0)
  const previewQuota = currentQuota + Number(addQuotaDelta || 0)

  return (
    <>
      <Modal
        title={user ? `编辑用户 · ${user.username}` : '编辑用户'}
        visible={visible}
        onCancel={onCancel}
        onOk={submit}
        style={{ width: isMobile ? modalWidth(560) : 560 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item field="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item field="username" label="用户名">
            <Input disabled />
          </Form.Item>
          <Form.Item field="displayName" label="显示名">
            <Input />
          </Form.Item>
          <Form.Item field="email" label="邮箱">
            <Input />
          </Form.Item>
          <Form.Item field="remark" label="备注" extra="仅管理员可见">
            <Input placeholder="内部备注" allowClear />
          </Form.Item>
          <Form.Item
            field="group"
            label="用户所属分组"
            extra="用于用户组 × 使用组倍率覆盖；可选择已声明分组，也可输入历史自定义分组。"
          >
            <Select
              placeholder="default"
              allowCreate
              showSearch
              options={mergedGroupOptions.map((group) => ({ value: group, label: group }))}
            />
          </Form.Item>
          <Form.Item
            field="quota"
            label="额度 (quota)"
            extra={`当前约 ${renderQuota(currentQuota)}`}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <div className="-mt-2 mb-4">
            <Button
              type="outline"
              size="small"
              icon={<IconPlus />}
              onClick={() => setAddQuotaOpen(true)}
            >
              添加额度
            </Button>
          </div>
          <Form.Item field="role" label="角色">
            <Select
              options={[
                { value: 1, label: '普通用户' },
                { value: 10, label: '管理员' },
                { value: 100, label: '超级管理员' },
              ]}
            />
          </Form.Item>
          <Form.Item field="password" label="重置密码" extra="留空表示不修改">
            <Input.Password placeholder="输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="添加额度"
        visible={addQuotaOpen}
        onOk={applyAddQuota}
        onCancel={() => {
          setAddQuotaOpen(false)
          setAddQuotaDelta(undefined)
          setAddAmountDelta(undefined)
        }}
        style={{ width: isMobile ? modalWidth(420) : 420 }}
      >
        <Typography.Text type="secondary" className="block mb-3">
          新额度：{renderQuota(currentQuota)} + {renderQuota(addQuotaDelta || 0)} ={' '}
          {renderQuota(previewQuota)}
        </Typography.Text>
        {showAmountInput && (
          <div className="mb-3">
            <Typography.Text className="block mb-1 text-sm">
              金额
              <Typography.Text type="secondary" className="ml-1 text-xs">
                （仅用于换算，实际保存的是额度）
              </Typography.Text>
            </Typography.Text>
            <InputNumber
              prefix={currencyConfig.prefix}
              placeholder="输入金额"
              value={addAmountDelta}
              precision={2}
              onChange={(val) => {
                setAddAmountDelta(val)
                if (val != null) {
                  setAddQuotaDelta(displayAmountToQuota(Math.abs(val)) * Math.sign(val))
                } else {
                  setAddQuotaDelta(undefined)
                }
              }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <div>
          <Typography.Text className="block mb-1 text-sm">额度增量</Typography.Text>
          <InputNumber
            placeholder="输入额度"
            value={addQuotaDelta}
            onChange={(val) => {
              setAddQuotaDelta(val)
              if (val != null) {
                setAddAmountDelta(
                  Number((quotaToDisplayAmount(Math.abs(val)) * Math.sign(val)).toFixed(2))
                )
              } else {
                setAddAmountDelta(undefined)
              }
            }}
            style={{ width: '100%' }}
            step={500000}
          />
        </div>
      </Modal>

      <SecureVerifyModal
        visible={verifyVisible}
        scope={verifyScope}
        context={verifyContext}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title="修改用户敏感信息需要安全验证"
      />
    </>
  )
}
